import React, { FC, useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Formik } from 'formik';

import {
  getMyCompanyDemographics,
  getMyUserDemographics,
  updateMyDemographics
} from '../../../lib/Api/participant/Event';
import { getUserRegistrationData } from '../../../lib/Api/participant/Team';

import Modal from '../Modal';
import SelectDropdown from '../SelectDropdown';
import LoadingIndicator from '../LoadingIndicator';

import { participantRegisterDemographics } from '../../../assets/img';
import {
  DemographicsModalContainer,
  ModalGraphic,
  ModalContent,
  ButtonWrapper,
  Submit
} from './Styles';
import { DarkBlueBodyS, BoldText, DarkBlueHeaderL } from '../../../Themes';

import { ApplicationState } from '../../../lib/Store';
import { CompanyDemographic, RoleCode, APIThunkDispatch } from '../../../lib/Types';

interface DemographicAnswer {
  companyDemographicId: number;
  userDemographicId: number | null;
  childDemographicId: number | null;
  childDemographicAnswer: string | null;
}

/**
 * Blocking modal that forces participants to answer their company's demographic
 * questions if they were somehow registered without them (e.g. the registration
 * race condition where `companyDemographics` was empty at submit time).
 *
 * - Shows for participants (Employee role) regardless of registration status.
 * - Cannot be dismissed: no close button, no footer/cancel, no backdrop close.
 * - Persists answers through the profile endpoint (`updateUserProfile`), keeping
 *   all other profile info as-is.
 */
const CompleteDemographicsModal: FC = () => {
  const dispatch: APIThunkDispatch = useDispatch();

  const {
    code,
    companyDemographics,
    userDemographics,
    company,
    team,
    individualEventRegistration
  } = useSelector(
    ({
      permissionsState,
      participantEventState,
      userState,
      participantTeamState
    }: ApplicationState) => ({
      code: permissionsState.code,
      companyDemographics: participantEventState.companyDemographics,
      userDemographics: participantEventState.userDemographics,
      company: userState.userCompany,
      team: participantTeamState.team,
      individualEventRegistration: participantTeamState.individualEventRegistration
    })
  );

  const isEmployee = code === RoleCode.Employee;
  // Registered == has an individual registration or belongs to a team (mirrors
  // the check used by EmployeeDashboardSwitch).
  const isRegistered = !!(individualEventRegistration || team);

  const [loading, setLoading] = useState(true);

  // Make sure we have the freshest registration + demographic data before deciding
  // whether to force the participant to answer.
  useEffect(() => {
    if (!isEmployee) return;
    let isMounted = true;
    (async () => {
      await Promise.all([
        dispatch(getUserRegistrationData()),
        dispatch(getMyCompanyDemographics()),
        dispatch(getMyUserDemographics())
      ]);
      if (isMounted) setLoading(false);
    })();
    return () => {
      isMounted = false;
    };
  }, [dispatch, isEmployee]);

  // Merge any existing answers onto the company's full question set.
  const answers: DemographicAnswer[] = useMemo(
    () =>
      companyDemographics.map(cd => {
        const existing = userDemographics.find(
          ud => ud.companyDemographicId === cd.companyDemographicId
        );
        return {
          companyDemographicId: cd.companyDemographicId,
          userDemographicId: existing?.userDemographicId ?? null,
          childDemographicId: existing?.childDemographicId ?? null,
          childDemographicAnswer: existing?.childDemographicAnswer ?? null
        };
      }),
    [companyDemographics, userDemographics]
  );

  // Number of company questions this participant has actually answered.
  const answeredCount = answers.filter(a => a.childDemographicId || a.childDemographicAnswer).length;

  // Only block when ALL of these hold:
  //  - user is a participant
  //  - data has loaded
  //  - the participant IS registered (unregistered users answer during registration)
  //  - the company actually has demographic questions
  //  - answered count != total company questions (i.e. some are missing)
  // Otherwise (not registered, or registered with everything answered) the modal
  // must not show and the participant continues as normal.
  if (
    !isEmployee ||
    loading ||
    !isRegistered ||
    companyDemographics.length === 0 ||
    answeredCount === companyDemographics.length
  ) {
    return null;
  }

  return (
    <Formik
      initialValues={{ demographics: answers }}
      enableReinitialize
      validateOnBlur={false}
      validateOnChange
      validate={({ demographics }) => {
        const errors: { demographics: { [key: string]: { error: string } } } = {
          demographics: {}
        };
        demographics.forEach((demo, index) => {
          if (!demo.childDemographicId && !demo.childDemographicAnswer) {
            errors.demographics[index] = { error: 'Each demographic must be answered' };
          }
        });
        if (Object.keys(errors.demographics).length > 0) return errors;
        return {};
      }}
      onSubmit={async (values, { setSubmitting }) => {
        // Defensive guard mirroring the validator: never persist unless every
        // company demographic question has an answer.
        if (
          values.demographics.length !== companyDemographics.length ||
          values.demographics.some(d => !d.childDemographicId && !d.childDemographicAnswer)
        ) {
          return;
        }
        setSubmitting(true);
        await dispatch(
          updateMyDemographics(
            values.demographics.map(d => ({
              companyDemographicId: d.companyDemographicId,
              childDemographicId: d.childDemographicId,
              childDemographicAnswer: d.childDemographicAnswer
            }))
          )
        );
        // updateMyDemographics updates userDemographics in the store with the full
        // answer list; once complete the modal condition becomes false and this
        // component unmounts.
        setSubmitting(false);
      }}
    >
      {({ values, errors, handleSubmit, setFieldValue, isSubmitting }) => {
        const getValue = (demoField: CompanyDemographic): number => {
          const currentAnswer = values.demographics.find(
            ud => ud.companyDemographicId === demoField.companyDemographicId
          );
          if (!currentAnswer) return undefined;
          const value = demoField.options?.find(
            o => o.companyDemographicId === currentAnswer.childDemographicId
          );
          return value?.companyDemographicId;
        };
        return (
          <Modal
            overflowVisible
            padding="40px"
            top={10}
            titlePadding="0px"
            showHeader={false}
            showFooter={false}
          >
            <DemographicsModalContainer>
              <ModalGraphic src={participantRegisterDemographics} />
              <ModalContent>
                <DarkBlueHeaderL>Complete your demographics</DarkBlueHeaderL>
                <DarkBlueBodyS padding={'0 20% 3rem 0'}>
                  {'Please answer the questions below to finish setting up your profile'}
                  {company?.name ? (
                    <>
                      {' for '}
                      <BoldText>{company.name}</BoldText>
                    </>
                  ) : null}
                  {'. You must complete these before you can continue.'}
                </DarkBlueBodyS>
                {companyDemographics.map((demoField, index) => (
                  <div key={index}>
                    <SelectDropdown
                      required
                      data={demoField.options}
                      value={getValue(demoField)}
                      error={errors && errors.demographics && errors.demographics[index]}
                      valueProp="companyDemographicId"
                      displayProp="name"
                      placeholder={'Select a ' + demoField.name}
                      onChange={value => {
                        const currentAnswerIndex = values.demographics.findIndex(
                          ud => ud.companyDemographicId === demoField.companyDemographicId
                        );
                        setFieldValue(`demographics[${currentAnswerIndex}]`, {
                          ...values.demographics[currentAnswerIndex],
                          companyDemographicId: demoField.companyDemographicId,
                          childDemographicId: value,
                          childDemographicAnswer: null
                        });
                      }}
                    />
                    <br />
                  </div>
                ))}
                <ButtonWrapper>
                  <Submit
                    type="button"
                    onClick={() => handleSubmit()}
                    variant="report"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? <LoadingIndicator /> : 'Complete'}
                  </Submit>
                </ButtonWrapper>
              </ModalContent>
            </DemographicsModalContainer>
          </Modal>
        );
      }}
    </Formik>
  );
};

export default CompleteDemographicsModal;
